import { Globe, X } from 'lucide-react';

interface IntegrationOptionsFormProps {
  isOpen: boolean;
  onClose: () => void;
  onStartSetup?: () => void;
}

export function IntegrationOptionsForm({ isOpen, onClose, onStartSetup }: IntegrationOptionsFormProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[140] p-3 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl w-full max-w-[900px] max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-200 [&_*]:!text-black [&_p]:!text-gray-700 [&_li]:!text-gray-600 [&_h2]:!text-black [&_h4]:!text-gray-800">
        {/* Header Section */}
        <div className="pb-3">
          <div className="mt-3 mb-[10px]">
            <h2 className="text-lg sm:text-xl font-bold pl-5">Integration Options</h2>
          </div>
          
          {/* Divider */}
          <div className="border-b border-gray-300"></div>
          
          {/* Description Text */}
          <div className="mt-3 pl-5 space-y-2">
            <p className="text-sm leading-relaxed">
              Your ORA agent isn't limited to a small set of integrations — it can work with any service that has a usable web interface, meaning anything you can open in a browser, click, type into, or navigate. That makes the ecosystem extremely robust!
            </p>
            <p className="text-sm leading-relaxed italic">
              Here's a comprehensive, platform‑agnostic list of categories and examples your agent can typically interact with.
            </p>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-4 sm:p-6 space-y-5">
          {/* Productivity & Office Suites */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Productivity & Office Suites</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>Outlook.com</li>
              <li>Office.com (Word, Excel, PowerPoint, OneDrive)</li>
              <li>Google Workspace (Gmail, Calendar, Drive, Docs, Sheets)</li>
              <li>Zoho (Mail, Docs, CRM)</li>
              <li>Proton Mail (web UI)</li>
              <li>iCloud (web apps)</li>
            </ul>
          </div>

          {/* Cloud Storage & File Management */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Cloud Storage & File Management</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>Dropbox</li>
              <li>Box</li>
              <li>Google Drive</li>
              <li>OneDrive</li>
              <li>SharePoint Online</li>
              <li>Egnyte</li>
              <li>pCloud</li>
              <li>Mega (web UI)</li>
            </ul>
          </div>

          {/* Scheduling & Booking Platforms */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Scheduling & Booking Platforms</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>Calendly</li>
              <li>Google Calendar</li>
              <li>Outlook Calendar</li>
              <li>Doodle</li>
              <li>Acuity Scheduling</li>
              <li>Setmore</li>
              <li>Any web‑based appointment system</li>
              <li>Eventbrite (event creation/management)</li>
            </ul>
          </div>

          {/* Communication & Collaboration Tools */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Communication & Collaboration Tools</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>Slack (web version)</li>
              <li>Microsoft Teams (web version)</li>
              <li>Zoom (web portal)</li>
              <li>Webex (web portal)</li>
              <li>Discord (web version)</li>
              <li>Mattermost</li>
              <li>Workplace by Meta</li>
            </ul>
          </div>

          {/* Collaboration and Social Communication */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Collaboration and Social Communication</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>LinkedIn (web UI)</li>
              <li>Facebook Business Suite</li>
              <li>Twitter/X (web UI)</li>
              <li>Instagram (web UI)</li>
              <li>TikTok Business Center</li>
              <li>YouTube Studio</li>
              <li>WhatsApp Web</li>
              <li>Telegram Web</li>
              <li>WeChat Web</li>
              <li>Skype (web version)</li>
              <li>Google Meet</li>
              <li>Microsoft Yammer</li>
              <li>Chatter (Salesforce)</li>
              <li>Reddit (web UI)</li>
              <li>Pinterest (web UI)</li>
            </ul>
          </div>

          {/* Project & Task Management */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Project & Task Management</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>Asana</li>
              <li>Trello</li>
              <li>Monday.com</li>
              <li>ClickUp</li>
              <li>Jira</li>
              <li>Basecamp</li>
              <li>Notion</li>
              <li>Airtable</li>
              <li>Smartsheet</li>
              <li>Wrike</li>
            </ul>
          </div>

          {/* Forms, Workflows & Automation */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Forms, Workflows & Automation</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>Google Forms</li>
              <li>Microsoft Forms</li>
              <li>Typeform</li>
              <li>Jotform</li>
              <li>Wufoo</li>
              <li>Formstack</li>
              <li>ServiceNow</li>
              <li>SAP SuccessFactors (web UI)</li>
              <li>Workday (web UI)</li>
              <li>Any custom form‑based workflow</li>
            </ul>
          </div>

          {/* CRM & Sales Platforms */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">CRM & Sales Platforms</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>HubSpot</li>
              <li>Salesforce (web UI)</li>
              <li>Zoho CRM</li>
              <li>Pipedrive</li>
              <li>Freshsales</li>
              <li>Copper CRM</li>
              <li>Keap</li>
              <li>Insightly</li>
            </ul>
          </div>

          {/* Document Signing & Legal */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Document Signing & Legal</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>DocuSign</li>
              <li>Adobe Sign</li>
              <li>PandaDoc</li>
              <li>HelloSign</li>
              <li>SignNow</li>
            </ul>
          </div>

          {/* E‑commerce & Payment Platforms */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">E‑commerce & Payment Platforms</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>Shopify (admin portal)</li>
              <li>WooCommerce dashboards</li>
              <li>Stripe Dashboard</li>
              <li>PayPal Business</li>
              <li>Square Dashboard</li>
              <li>Etsy Shop Manager</li>
              <li>Amazon Seller Central</li>
            </ul>
          </div>

          {/* Knowledge & Content Platforms */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Knowledge & Content Platforms</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>Confluence</li>
              <li>SharePoint Sites</li>
              <li>Notion</li>
              <li>Coda</li>
              <li>GitHub (web UI)</li>
              <li>GitLab (web UI)</li>
              <li>Medium</li>
              <li>WordPress Admin</li>
            </ul>
          </div>

          {/* HR, Recruiting & Talent Platforms */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">HR, Recruiting & Talent Platforms</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>LinkedIn (web UI)</li>
              <li>Indeed Employer</li>
              <li>Greenhouse</li>
              <li>Lever</li>
              <li>BambooHR</li>
              <li>Workday</li>
              <li>ADP (web UI)</li>
            </ul>
          </div>

          {/* Finance & Accounting Tools */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Finance & Accounting Tools</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>QuickBooks Online</li>
              <li>Xero</li>
              <li>FreshBooks</li>
              <li>Wave</li>
              <li>Expensify</li>
              <li>Bill.com</li>
            </ul>
          </div>

          {/* IT & DevOps Tools */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">IT & DevOps Tools</h4>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-2">
              <li>Azure DevOps (web UI)</li>
              <li>GitHub</li>
              <li>GitLab</li>
              <li>Bitbucket</li>
              <li>Jenkins dashboards</li>
              <li>Grafana dashboards</li>
              <li>Datadog (web UI)</li>
            </ul>
          </div>

          {/* The Rule of Thumb */}
          <div className="pt-2">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">The Rule of Thumb</h4>
            <p className="text-sm text-gray-600 mb-2">If it runs in a browser:</p>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-4 mb-2">
              <li>has buttons, menus, or forms</li>
              <li>doesn't block automation</li>
            </ul>
            <p className="text-sm text-gray-600">your agent can typically navigate and connect to it.</p>
          </div>

          {/* Close Button */}
          <div className="flex justify-center pt-4">
            <button
              onClick={() => {
                if (onStartSetup) {
                  onStartSetup();
                }
                onClose();
              }}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
            >
              Got it! I'm ready to get started!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}